import { useMemo } from "react";
import type { KioskCacheItem, LocationCacheItem } from "../../../types/session.ts";
import type { AdvancedFilterProps } from "./types.ts";
import { FreeTextIdentityFilter } from "./freeTextIdentityFilter.tsx";
import type { FreeTextIdentityConfig, GroupFieldDef } from "./freeTextIdentityFilter.tsx";
import { formatOperatorId, useOperatorNameById } from "./identityVariants/shared.tsx";
import { normalizeInHouseStatus, rankInHouseStatus } from "./inHouseStatus.ts";
import { useSiblingCheckbox } from "./filterSiblings.ts";

/** A kiosk is "live" when its numeric LiveStatus column = 1 — same definition
 *  KioskLocationTool uses (`isKioskLive`). InHouseStatus / TestString are NOT
 *  reliable test markers: many production kiosks carry non-empty TestString
 *  values (see endpointMarkers.ts comment), and InHouseStatus may be unset
 *  on legacy rows. Aligning with LiveStatus keeps this filter consistent
 *  with what other tools call "live kiosks". */
function isKioskLive(k: KioskCacheItem): boolean {
  return k.LiveStatus === 1;
}

function templateFn(item: KioskCacheItem): string {
  let s = String(item.KioskID);
  if (item.StoreName) s += ", " + item.StoreName;
  if (item.Address) s += ", " + item.Address;
  if (item.City) s += ", " + item.City;
  if (item.District) s += ", " + item.District;
  if (item.IpAddress) s += ", " + item.IpAddress;
  if (item.ExternalReference) s += " (" + item.ExternalReference + ")";
  return s;
}

function matchFn(item: KioskCacheItem, text: string): boolean {
  const lc = text.toLowerCase();
  return (
    item.KioskID.toString().indexOf(lc) === 0 ||
    (!!item.IpAddress && item.IpAddress.indexOf(lc) >= 0) ||
    (!!item.StoreName && item.StoreName.toLowerCase().indexOf(lc) >= 0) ||
    (!!item.Address && item.Address.toLowerCase().indexOf(lc) >= 0) ||
    (!!item.City && item.City.toLowerCase().indexOf(lc) >= 0) ||
    (!!item.District && item.District.toLowerCase().indexOf(lc) >= 0) ||
    (!!item.ExternalReference && item.ExternalReference.toLowerCase().indexOf(lc) >= 0)
  );
}

export function KioskFilter({ info, isParameter, value, onChange, onSearch, cache }: AdvancedFilterProps) {
  const items = (cache.CommonData?.Kiosk ?? []) as KioskCacheItem[];
  const locations = (cache.CommonData?.Location ?? []) as LocationCacheItem[];

  // Join each kiosk with its location so display name + all location-owned
  // group fields resolve as plain properties on the enriched record. Matches
  // WebAdmin2's in-memory join: server kiosk rows don't carry StoreName /
  // Address / City / District — those live on Location and we overwrite the
  // kiosk's (empty) copy with the location's real values.
  const enriched = useMemo(() => {
    const locById = new Map<number, LocationCacheItem>();
    for (const l of locations) locById.set(l.LocationID, l);
    return items.map((k) => {
      const l = (locById.get(k.LocationID) ?? {}) as Record<string, unknown>;
      const kk = k as unknown as Record<string, unknown>;
      const pick = (locKey: string, kioskKey: string = locKey) => {
        const lv = l[locKey];
        if (lv != null && lv !== "") return String(lv);
        const kv = kk[kioskKey];
        return kv == null ? "" : String(kv);
      };
      return {
        ...k,
        InHouseStatus: k.InHouseStatus ?? "",
        StoreName: pick("StoreName") || k.StoreName || "",
        Address: pick("Address") || k.Address || "",
        City: pick("City") || k.City || "",
        District: pick("District") || k.District || "",
        Chain: pick("Chain"),
        Country: pick("Country"),
        ZipCode: pick("ZipCode"),
        County: pick("County"),
        Suburb: pick("Suburb"),
        KioskDefined1: (kk.KioskDefined1 as string | undefined) ?? "",
        KioskDefined2: (kk.KioskDefined2 as string | undefined) ?? "",
        KioskDefined3: (kk.KioskDefined3 as string | undefined) ?? "",
        LocationDefined1: (l.LocationDefined1 as string | undefined) ?? "",
        LocationDefined2: (l.LocationDefined2 as string | undefined) ?? "",
        LocationDefined3: (l.LocationDefined3 as string | undefined) ?? "",
        OwnerOperatorID: (l.OwnerOperatorID as number | undefined) ?? k.OwnerOperatorID ?? 0,
      };
    });
  }, [items, locations]);

  type EnrichedKiosk = typeof enriched[number];

  // Sibling "Include Test Kiosks" checkbox. Checked → include test rigs;
  // unchecked → exclude. Absent (no such sibling on this grid) → include,
  // so grids that never offered the choice don't silently lose data.
  const includeTest = useSiblingCheckbox("Include Test Kiosks") ?? true;

  const visible = useMemo(
    () => (includeTest ? enriched : enriched.filter((k) => isKioskLive(k))),
    [enriched, includeTest],
  );

  // Order suggestions by InHouseStatus rank (LIVE first, ORDERED last) then by
  // KioskID. Unknown / empty status is treated as TEST. The variant renderers
  // walk this list top-to-bottom until they hit their 60-suggestion cap, so
  // putting live rigs at the top is the only way to ensure they're visible
  // without typing a very specific search.
  const sorted = useMemo(
    () => [...visible].sort((a, b) => {
      const r = rankInHouseStatus(a.InHouseStatus) - rankInHouseStatus(b.InHouseStatus);
      return r !== 0 ? r : a.KioskID - b.KioskID;
    }),
    [visible],
  );

  const operatorNameById = useOperatorNameById(cache.CommonData?.Common);

  /**
   * Group options, matching the legacy client's list. Fields named the same
   * as the cache properties resolve automatically; the Defined fields and the
   * operator-name lookup go through getValue.
   */
  const groupFields = useMemo<GroupFieldDef[]>(() => [
    { label: "Address", field: "Address" },
    { label: "Retail Host", field: "Chain" },
    { label: "City", field: "City" },
    { label: "County", field: "County" },
    { label: "State", field: "District" },
    { label: "Kiosk Group #1", field: "KioskDefined1" },
    { label: "Kiosk Group #2", field: "KioskDefined2" },
    { label: "Kiosk Group #3", field: "KioskDefined3" },
    { label: "Location Group #1", field: "LocationDefined1" },
    { label: "Location Group #2", field: "LocationDefined2" },
    { label: "Location Group #3", field: "LocationDefined3" },
    { label: "Store Name", field: "StoreName" },
    { label: "Suburb", field: "Suburb" },
    { label: "Type", field: "Type" },
    {
      label: "InHouseStatus",
      field: "InHouseStatus",
      getValue: (item) => normalizeInHouseStatus((item as EnrichedKiosk).InHouseStatus),
    },
    { label: "Zip", field: "ZipCode" },
    { label: "Country", field: "Country" },
    {
      label: "Owner/Operator ID",
      field: "OwnerOperatorID",
      getValue: (item) => formatOperatorId((item as EnrichedKiosk).OwnerOperatorID, operatorNameById),
    },
  ], [operatorNameById]);

  const config = useMemo<FreeTextIdentityConfig<EnrichedKiosk>>(
    () => ({
      idField: "KioskID",
      nameField: "StoreName",
      items: sorted,
      matchFn,
      templateFn,
      groupFields,
      getIdFromItem: (item) => item.KioskID,
      extraMeta: (item) => (item.ExternalReference ? item.ExternalReference : undefined),
    }),
    [sorted, groupFields],
  );

  return (
    <FreeTextIdentityFilter
      config={config}
      columnName={info.ColumnName}
      isParameter={isParameter}
      value={value}
      onChange={onChange}
      onSearch={onSearch}
    />
  );
}
